import { Delete } from "@mui/icons-material";
import { CircularProgress, Icon, IconButton, TableCell, TextField, Tooltip, } from "@mui/material";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import DescontoInput from "pages/registra-venda/DescontoInput";
import { IProdutoRow } from "pages/registra-venda/registra-venda.model";
import { memo, useCallback } from "react";
import { setBrinde } from "redux/features/stateSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { FichaImagemAssociacao } from "redux/features/imagemReducer";
import { theme } from "theme";
import useCustomLocation from "utils/useCustomLocation";
import { currencyOf } from "utils/util";
import MiniImagem from "./ProdutoImagem/MiniImagem";

type ItemRegistraVendaProps = {
    row: IProdutoRow;
    imagens: Array<FichaImagemAssociacao>
    onChangeQuantidade: (uuid: string, value: string) => void;
    changeDesconto: (value: number, uuid: string) => void;
    toggleTipoDesconto: (uuid: string) => void;
    onBlurDescontoItem: (value: number, uuid: string) => void;
    saveTempDesconto: (prevDesconto: number, uuid: string) => void;
    onClickDelete: (uuid: string, produto: IProdutoRow) => void;
    onOpenImage: (uuidProduto: string) => void;
    disabled?: boolean;
    canDescontoItem?: boolean;
    canBrinde?: boolean;
};

const ItemRegistraVenda = (props: ItemRegistraVendaProps) => {
    const { canDescontoItem = true, canBrinde = true } = props;
    const { addError } = useSnackbarCustom();
    const { itens } = useAppSelector(e => e.state.pedido);
    const dispatch = useAppDispatch();
    const {
        isTelaRegistraVenda
    } = useCustomLocation();

    const { tmfBrinde, somarValorBrindeTotalNota } = useAppSelector(e => e.configuracoes.configuracaoPedidoBalcao);

    const onClickBrinde = useCallback((uuid: string) => {
        if (!tmfBrinde) {
            return addError({ message: 'Brinde sem configuração de movimentações fiscais.', closeable: true });
        }
        dispatch(setBrinde({ uuid, zerarValorTotal: !somarValorBrindeTotalNota }));
        //eslint-disable-next-line
    }, [tmfBrinde, somarValorBrindeTotalNota]);

    const zeraValorPorBrinde = Boolean(props.row.brinde) && !somarValorBrindeTotalNota;

    return (
        <>
            <MiniImagem
                onOpenImage={props.onOpenImage}
                imagens={props.imagens.filter(e => e.uuidProduto === props.row.produto.uuidProduto)}
                uuidProduto={props.row.produto.uuidProduto}
                descritivo={props.row.produto.descritivo}
            />
            <TableCell sx={{
                color: theme.palette.common.black,
                padding: 0,
                paddingLeft: '15px',
            }}>
                {`${props.row.produto.codigoSku} - ${props.row.produto.nome} / ${props.row.produto.cor} / ${props.row.produto.tamanho}`}
            </TableCell>

            <TableCell sx={{
                color: theme.palette.common.black,
                padding: 0,
                textAlign: 'center',
                paddingBottom: '10px'
            }}
                align="center">
                {props.row.estoque === -1 ?
                    <CircularProgress
                        size={12}
                        color='primary'
                    />
                    :
                    <TextField
                        error={props.row.quantidade > props.row.estoque}
                        inputProps={{ inputMode: 'numeric', min: 1, style: { textAlign: 'center', color: (props.row.estoque === 0 || (props.row.quantidade > props.row.estoque) ? theme.palette.error.main : "") } }}
                        value={props.row.quantidade}
                        onKeyDown={e => e.key === '.' && e.preventDefault()}
                        onChange={q => { props.onChangeQuantidade(props.row.uuid, q.target.value); }}
                        variant="standard"
                        type="number"
                        disabled={props.disabled}
                    />
                }
            </TableCell>

            <TableCell align="center" sx={{
                color: theme.palette.common.black,
                padding: 0,
                textAlign: 'center'
            }}>
                {currencyOf(zeraValorPorBrinde ? 0 : props.row.produto.preco)}
            </TableCell>

            <TableCell align="center" sx={{
                color: theme.palette.common.black,
                padding: 0,
                textAlign: 'center',
                paddingBottom: '18px'
            }}>
                <DescontoInput
                    item={{
                        tipoDesconto: props.row.itemDesconto.tipoDesconto,
                        valorDesconto: props.row.itemDesconto.valorDesconto,
                        percentualDesconto: props.row.itemDesconto.percentualDesconto,
                        valorTotalBruto: props.row.produto.preco * props.row.quantidade,
                        onChangeDesconto: props.changeDesconto,
                        uuid: props.row.uuid
                    }}
                    inputProps={{ 'data-testid': 'item-desconto-input', style: { textAlign: 'center', } }}
                    iconButtonProps={{
                        disabled: Boolean(props.row.brinde),
                        onToggleTipoDesconto: props.toggleTipoDesconto,
                        percentoProps: {
                            color: theme.palette.primary.main,
                        },
                        valorProps: {
                            color: theme.palette.primary.main,
                        }
                    }}
                    onBlur={props.onBlurDescontoItem}
                    onSaveTempDesconto={props.saveTempDesconto}
                    isVisualizar={!canDescontoItem || Boolean(props.row.brinde)}
                    origem="ROW"
                />
            </TableCell>

            <TableCell align="center" sx={{
                color: (props.row.brinde) ? theme.palette.text.disabled : theme.palette.common.black,
                padding: 0,
                textAlign: 'center'
            }}>
                {currencyOf(zeraValorPorBrinde ? 0 : props.row.valorTotal)}
            </TableCell>

            <TableCell align="right" sx={{
                color: (props.row.brinde) ? theme.palette.text.disabled : theme.palette.common.black,
                textAlign: 'center'
            }}>
                {isTelaRegistraVenda() &&
                    <IconButton
                        onClick={() => onClickBrinde(props.row.uuid)}
                        disabled={!canBrinde}
                        style={{ color: !canBrinde ? theme.palette.text.disabled : itens.find(e => e.uuid === props.row.uuid)?.brinde ? theme.palette.success.main : theme.palette.text.disabled }}
                    >
                        <Tooltip title={props.row.brinde ? "Clique para inativar o produto de brinde" : "Clique para ativar o produto de brinde"}>
                            <Icon className="fa-solid fa-gifts" sx={{ width: "30px" }} />
                        </Tooltip>
                    </IconButton>
                }
            </TableCell>

            <TableCell align="right" sx={{
                textAlign: 'center'
            }}>
                <IconButton
                    onClick={() => props.onClickDelete(props.row.uuid, props.row)}
                    disabled={props.disabled}
                    style={{ color: props.disabled ? theme.palette.text.disabled : theme.palette.primary.main }}
                >
                    <Delete />
                </IconButton>
            </TableCell>
        </ >
    );
};

export default memo(ItemRegistraVenda);
